<template>
  <!-- 项目迭代批次管理 -->
  <div class="sprint-list">
    <list-page>
      <search-condition @search="resetSearch" :show-search="true">
        <template #default>
          <el-input v-model="searchInfo.searchKey" placeholder="批次编码/名称" @keydown.enter="resetSearch" clearable>
            <template #prefix>关键字</template>
          </el-input>
          <el-select v-model="searchInfo.projectId" placeholder="全部项目" clearable filterable @change="resetSearch">
            <template #prefix>所属项目</template>
            <el-option v-for="p in projectOptions" :key="p.id" :label="p.projectName" :value="p.id"/>
          </el-select>
        </template>
        <template #option>
          <page-button type="read" el-type="primary" @click="resetSearch">查询</page-button>
          <page-button type="write" el-type="success" @click="openDialog()">+ 新建迭代批次</page-button>
        </template>
      </search-condition>
      <list-table>
        <yq-table :data="dataList" height="100%" stripe border page-name="sprintList">
          <yq-table-column label="批次编码" prop="sprintCode" width="160rem">
            <template v-slot="{row}">
              <span class="code-text">{{ row.sprintCode || '—' }}</span>
            </template>
          </yq-table-column>
          <yq-table-column label="批次名称" prop="sprintName" min-width="170rem">
            <template v-slot="{row}">
              <strong>{{ row.sprintName || '—' }}</strong>
              <div v-if="row.goal" class="muted ellipsis">{{ row.goal }}</div>
            </template>
          </yq-table-column>
          <yq-table-column label="所属项目" prop="projectName" min-width="160rem">
            <template v-slot="{row}">
              <div>{{ row.projectName || '—' }}</div>
              <div v-if="row.projectCode" class="muted code-text">{{ row.projectCode }}</div>
            </template>
          </yq-table-column>
          <yq-table-column label="项目经理" prop="managerName" width="105rem">
            <template v-slot="{row}">
              {{ row.managerName || '—' }}
            </template>
          </yq-table-column>
          <yq-table-column label="期望完成" prop="expectDate" width="110rem" align="center">
            <template v-slot="{row}">
              {{ dateOnly(row.expectDate) }}
            </template>
          </yq-table-column>
          <yq-table-column label="起止日期" prop="startDate" width="175rem" align="center">
            <template v-slot="{row}">
              <span v-if="row.startDate || row.endDate">{{ dateOnly(row.startDate) }} ~ {{ dateOnly(row.endDate) }}</span>
              <span v-else class="muted">—</span>
            </template>
          </yq-table-column>
          <yq-table-column label="状态" prop="state" width="95rem" align="center">
            <template v-slot="{row}">
              <el-tag size="small" :type="stateTag(row.state)">{{ row.state || '—' }}</el-tag>
            </template>
          </yq-table-column>
          <yq-table-column label="任务数" prop="taskCount" width="80rem" align="center">
            <template v-slot="{row}">
              {{ row.taskCount || 0 }}
            </template>
          </yq-table-column>
          <yq-table-column label="进度" prop="progress" width="150rem" align="center">
            <template v-slot="{row}">
              <el-progress :percentage="row.progress || 0" :stroke-width="10" :show-text="true" :format="fmtPercent"/>
            </template>
          </yq-table-column>
          <yq-table-column label="操作" prop="options" align="center" fixed="right" width="140rem" :show-overflow-tooltip="false">
            <template v-slot="{row}">
              <table-button @click="openDialog(row)">编辑</table-button>
              <table-button el-type="danger" message="删除后该批次下任务将解绑（任务保留），是否确认删除？" @click="deleteRow(row.id)">删除</table-button>
            </template>
          </yq-table-column>
        </yq-table>
      </list-table>
      <form-page v-model:page-number="searchInfo.pageNumber" v-model:page-size="searchInfo.pageSize"
                 :total-count="searchInfo.totalCount" @onLoad="queryList"/>
    </list-page>

    <!-- 新建/编辑迭代弹窗 -->
    <el-dialog v-model="showDialog" :title="dialogData.id ? '编辑迭代批次' : '新建迭代批次'" dialog-width="2" :close-on-click-modal="false">
      <form-items :data="dialogData" :columns="2" label-width="2.4rem" icon-width="0" ref="dialogForm">
        <form-item label="所属项目" :required="true" name="projectId" error-message="请选择所属项目">
          <el-select v-model="dialogData.projectId" placeholder="请选择所属项目" filterable style="width: 100%" @change="onProjectChange">
            <el-option v-for="p in projectOptions" :key="p.id" :label="p.projectName" :value="p.id"/>
          </el-select>
        </form-item>
        <form-item label="批次名称">
          <el-input v-model="dialogData.sprintName" placeholder="留空自动生成：项目名·第N批"/>
        </form-item>
        <form-item label="项目经理">
          <employee-select v-model="dialogData.managerId" placeholder="请选择项目经理" @change="onManagerChange"/>
        </form-item>
        <form-item label="期望完成">
          <el-date-picker v-model="dialogData.expectDate" type="date" value-format="YYYY-MM-DD" placeholder="期望完成时间" style="width: 100%"/>
        </form-item>
        <form-item label="开始日期">
          <el-date-picker v-model="dialogData.startDate" type="date" value-format="YYYY-MM-DD" placeholder="开始日期" style="width: 100%"/>
        </form-item>
        <form-item label="结束日期">
          <el-date-picker v-model="dialogData.endDate" type="date" value-format="YYYY-MM-DD" placeholder="结束日期" style="width: 100%"/>
        </form-item>
        <form-item label="批次目标" :show-block="true">
          <el-input v-model="dialogData.goal" type="textarea" :rows="2" placeholder="本批次要交付的目标说明"/>
        </form-item>
        <form-item label="备注" :show-block="true">
          <el-input v-model="dialogData.remark" type="textarea" :rows="2" placeholder="备注信息"/>
        </form-item>
      </form-items>
      <template #footer>
        <page-button @click="showDialog = false">取消</page-button>
        <page-button type="write" el-type="primary" @click="saveData">保存</page-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import FormItems from "@/components/h5/layout/FormItems.vue";
import FormItem from "@/components/h5/layout/FormItem.vue";
import EmployeeSelect from "@/components/h5/data/EmployeeSelect.vue";
import {useRoute, useRouter} from "vue-router";
import Util from "@/util/util";
import sprintApi from "@/api/dz-project/project/sprintApi";

export default {
  setup() {
    return {router: useRouter(), route: useRoute()};
  },
  name: "SprintList",
  components: {FormItems, FormItem, EmployeeSelect},
  data() {
    return {
      searchInfo: {pageNumber: 1},
      dataList: [],
      projectOptions: [],
      showDialog: false,
      dialogData: {},
    };
  },
  beforeRouteLeave(to, from, next) {
    Util.cachePageData(this, to, from);
    next();
  },
  created() {
    this.initOptions();
    this.resetSearch();
  },
  methods: {
    async initOptions() {
      const list = await sprintApi.queryProjectOptions();
      this.projectOptions = list || [];
    },
    async queryList() {
      const {records, totalCount} = await sprintApi.queryList(this.searchInfo);
      this.dataList = records || [];
      this.searchInfo.totalCount = totalCount;
    },
    resetSearch() {
      this.searchInfo.pageNumber = 1;
      this.queryList();
    },
    // 迭代状态（服务层按日期推导）：未开始/进行中/已结束/排期中/已到期
    stateTag(state) {
      const map = {"排期中": "warning", "未开始": "info", "进行中": "success", "已结束": "info", "已到期": "danger"};
      return map[state] || "info";
    },
    fmtPercent(percentage) {
      return percentage + "%";
    },
    dateOnly(v) {
      return v ? String(v).slice(0, 10) : "—";
    },
    toDateValue(v) {
      return v ? String(v).slice(0, 10) : "";
    },
    onProjectChange(projectId) {
      const project = this.projectOptions.find(p => p.id === projectId);
      this.dialogData.projectName = project ? project.projectName : "";
      this.dialogData.projectCode = project ? project.projectCode : "";
    },
    onManagerChange(data) {
      this.dialogData.managerName = data ? data.employeeName : "";
    },
    openDialog(row) {
      if (row) {
        this.dialogData = JSON.parse(JSON.stringify(row));
        this.dialogData.expectDate = this.toDateValue(this.dialogData.expectDate);
        this.dialogData.startDate = this.toDateValue(this.dialogData.startDate);
        this.dialogData.endDate = this.toDateValue(this.dialogData.endDate);
      } else {
        this.dialogData = {sprintName: "", projectId: "", projectName: "", projectCode: "", managerId: "", managerName: "", expectDate: "", startDate: "", endDate: "", goal: "", remark: ""};
      }
      this.showDialog = true;
    },
    async saveData() {
      if (!this.$refs.dialogForm.validCheck()) { // 数据验证不通过
        return;
      }
      if (this.dialogData.startDate && this.dialogData.endDate && this.dialogData.startDate > this.dialogData.endDate) {
        this.$message.warning("开始日期不能晚于结束日期");
        return;
      }
      const payload = JSON.parse(JSON.stringify(this.dialogData));
      // 空日期置空，避免传空串导致后端日期解析失败
      payload.expectDate = payload.expectDate || null;
      payload.startDate = payload.startDate || null;
      payload.endDate = payload.endDate || null;
      await sprintApi.save(payload);
      this.showDialog = false;
      this.resetSearch();
    },
    async deleteRow(id) {
      await sprintApi.delete(id);
      this.resetSearch();
    },
  },
};
</script>

<style lang="scss" scoped>
.sprint-list {
  height: 100%;
  .code-text {
    font-family: Consolas, Menlo, monospace;
    font-size: 12px;
    color: var(--yq-font-color-desc);
  }
  .ellipsis {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}
</style>
